import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import BottomNav from "@/components/BottomNav";
import Logo from "@/components/Logo";
import { Play, Loader2, ShoppingBag, Search } from "lucide-react";
import { Link } from "react-router-dom";

export default function Feed() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIdx, setActiveIdx] = useState(0);
    const [muted, setMuted] = useState(true);
    const containerRef = useRef(null);
    const itemRefs = useRef([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/products");
                setProducts(data);
            } catch (err) {
                console.error("Feed load failed", err?.response?.status, err?.message);
                setProducts([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (!products.length) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting && e.intersectionRatio > 0.6) {
                        const idx = Number(e.target.getAttribute("data-idx"));
                        setActiveIdx(idx);
                    }
                });
            },
            { threshold: [0.6], root: containerRef.current }
        );
        itemRefs.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [products]);

    if (loading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-black">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF5722]" />
            </div>
        );
    }

    if (!products.length) {
        return (
            <div className="min-h-screen bg-[#050505] text-white flex flex-col">
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                    <div className="w-20 h-20 rounded-3xl bg-[#FF5722]/20 flex items-center justify-center mb-6">
                        <ShoppingBag className="w-10 h-10 text-[#FF5722]" />
                    </div>
                    <h2 className="font-display font-black text-3xl mb-2">Rien pour l'instant</h2>
                    <p className="text-white/60 font-body mb-6 max-w-xs">Soyez le premier à publier votre boutique en vidéo sur VIDMAR !</p>
                    <Link
                        to="/onboarding"
                        data-testid="empty-become-seller"
                        className="orange-btn rounded-full px-6 h-12 flex items-center gap-2 font-body font-semibold"
                    >
                        <Play className="w-4 h-4 fill-white" /> Devenir vendeur
                    </Link>
                </div>
                <BottomNav />
            </div>
        );
    }

    return (
        <div className="relative bg-black" data-testid="feed-page">
            {/* Top logo */}
            <div className="fixed top-4 left-4 right-4 z-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Logo size={36} />
                    <span className="font-display font-black text-xl text-white text-shadow-strong">VIDMAR</span>
                </div>
                <Link
                    to="/search"
                    data-testid="feed-search-btn"
                    className="w-11 h-11 rounded-full glass flex items-center justify-center active:scale-95"
                >
                    <Search className="w-5 h-5 text-white" />
                </Link>
            </div>

            <div ref={containerRef} className="feed-container">
                {products.map((p, idx) => (
                    <div
                        key={p.id}
                        data-idx={idx}
                        ref={(el) => (itemRefs.current[idx] = el)}
                    >
                        <VideoCard
                            product={p}
                            active={idx === activeIdx}
                            muted={muted}
                            onToggleMute={() => setMuted((m) => !m)}
                        />
                    </div>
                ))}
            </div>

            <BottomNav />
        </div>
    );
}
